import React, { useEffect, useState } from 'react';
import { MonitorPlay, Copy, ExternalLink, RefreshCw, Lock } from 'lucide-react';
import { Input } from '../ui/input';
import { DeptColorEditor } from './DeptColorEditor';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const PinEditor = ({ pinSet, onSaved }) => {
  const [pin, setPin] = useState('');
  const save = async (value) => {
    try {
      onSaved((await api.put('/api/clock/display-link/pin', { pin: value })).data);
      setPin('');
      toast({ title: value ? 'Full-screen PIN saved' : 'Full-screen PIN removed' });
    } catch (e) { toast({ title: 'Could not save PIN', description: e.response?.data?.detail, variant: 'destructive' }); }
  };
  return (
    <div className="border-t pt-3 space-y-1.5" data-testid="display-pin-editor">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700"><Lock className="h-3.5 w-3.5" /> Full-screen lock PIN
        <span className={`ml-auto text-[11px] font-normal ${pinSet ? 'text-emerald-600' : 'text-gray-400'}`} data-testid="display-pin-status">{pinSet ? 'PIN is set' : 'No PIN'}</span>
      </p>
      <p className="text-[11px] text-gray-500">When set, leaving full screen on the display asks for this PIN.</p>
      <div className="flex gap-1.5">
        <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" type="password" placeholder={pinSet ? 'New 4–6 digit PIN' : '4–6 digit PIN'} className="h-8 text-xs" data-testid="display-pin-new" />
        <Button size="sm" className="h-8" onClick={() => save(pin)} disabled={pin.length < 4} data-testid="display-pin-save">Save</Button>
        {pinSet && <Button size="sm" variant="ghost" className="h-8 text-rose-600" onClick={() => save('')} data-testid="display-pin-remove">Remove</Button>}
      </div>
    </div>
  );
};

export const DisplayLinkMenu = () => {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState(null);
  useEffect(() => { if (open) api.get('/api/clock/display-link').then((r) => setLink(r.data)).catch(() => {}); }, [open]);
  const url = link ? `${window.location.origin}/clock-display/${link.token}` : '';

  const copy = () => { navigator.clipboard?.writeText(url); toast({ title: 'Display link copied' }); };
  const toggle = async (active) => setLink((await api.put('/api/clock/display-link', { active })).data);
  const regen = async () => {
    if (!window.confirm('Create a new link? The old link will stop working on any screen using it.')) return;
    setLink((await api.post('/api/clock/display-link/regenerate')).data);
    toast({ title: 'New display link created' });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-700 bg-white" data-testid="display-link-btn">
          <MonitorPlay className="h-3.5 w-3.5 mr-1" /> Live display
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 space-y-3" align="end" data-testid="display-link-popover">
        <div>
          <p className="text-sm font-semibold">Live clock-in display</p>
          <p className="text-[11px] text-gray-500">Open this link on a TV or tablet to show who is clocked in now. No login needed — anyone with the link can view it.</p>
        </div>
        {link && (
          <>
            <div className="flex items-center gap-2 rounded border bg-gray-50 px-2 py-1.5">
              <span className={`flex-1 truncate text-xs ${link.active ? 'text-gray-700' : 'text-gray-400 line-through'}`} data-testid="display-link-url">{url}</span>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={copy} disabled={!link.active} data-testid="display-link-copy"><Copy className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => window.open(url, '_blank')} disabled={!link.active} data-testid="display-link-open"><ExternalLink className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <Switch checked={link.active} onCheckedChange={toggle} data-testid="display-link-toggle" />
                {link.active ? 'Link is on' : 'Link is off'}
              </label>
              <Button size="sm" variant="ghost" className="text-xs text-rose-600" onClick={regen} data-testid="display-link-regenerate">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> New link
              </Button>
            </div>
            <PinEditor pinSet={link.pin_set} onSaved={setLink} />
            <DeptColorEditor colors={link.dept_colors || {}} onSaved={setLink} />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
